// /rf-admin/tenants/new — 创建租户表单(阶段 A 入口)
// UI 复刻 /dashboard:Card + PageTitle + .field + .input/.select + .btn-primary
// 行为独立:rf-admin-shell 内页内容统一浅色风格

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { rfAdminCreateTenant, type RfTenantCreateResult } from "../api/admin";
import { Card } from "../../components/ui/Card";
import { PageTitle } from "../../components/ui/PageTitle";
import { Button } from "../../components/ui/Button";

export function RfAdminTenantNew() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [contractType, setContractType] = useState("standard");
  const [featuresCsv, setFeaturesCsv] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RfTenantCreateResult | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const features = featuresCsv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const r = await rfAdminCreateTenant({
        company_name: companyName,
        admin_email: adminEmail,
        admin_password: adminPassword,
        contract_type: contractType,
        features,
      });
      setResult(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <div className="rf-admin-page">
        <PageTitle
          title="テナント作成完了"
          subtitle="管理者ユーザーまで作成されました。設定完了メールはまだ送信されていません。"
        />
        <Card>
          <div className="rf-admin-success">
            <strong>{result.tenant_name}</strong> を作成しました。
            <ul>
              <li>postgres tenant_id: {result.postgres_tenant_id}</li>
              <li>postgres user_id: {result.postgres_user_id}</li>
              <li>tenant_code: {result.tenant_code}</li>
              <li>admin email: {result.admin_email}</li>
            </ul>
            <div>次はテナント詳細の「ユーザータブ」から「設定完了メールを送信」を実行してください。</div>
            <div className="rf-admin-success-actions">
              <Button variant="primary" onClick={() => navigate(`/rf-admin/tenants/${result.rf_tenant_id}`)}>
                テナント詳細を開く
              </Button>
              <Button variant="secondary" onClick={() => navigate("/rf-admin/tenants")}>
                一覧へ戻る
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="rf-admin-page">
      <PageTitle
        title="新規テナント作成"
        subtitle="テナント + 管理者ユーザーを作成します。設定完了メールは別途送付します。"
      />
      <Card>
        <form onSubmit={onSubmit} className="rf-admin-form">
          <div className="field">
            <label htmlFor="company_name" className="field-label">
              会社名 <span className="field-label-required">*</span>
            </label>
            <input
              id="company_name"
              type="text"
              className="input"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="admin_email" className="field-label">
              管理者 email <span className="field-label-required">*</span>
            </label>
            <input
              id="admin_email"
              type="email"
              className="input"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="admin_password" className="field-label">
              管理者パスワード <span className="field-label-required">*</span>
            </label>
            <input
              id="admin_password"
              type="password"
              className="input"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              required
              minLength={12}
            />
            <div className="field-hint">12文字以上、大小英字・数字・記号を含む</div>
          </div>

          <div className="field">
            <label htmlFor="contract_type" className="field-label">
              契約種別
            </label>
            <select
              id="contract_type"
              className="select"
              value={contractType}
              onChange={(e) => setContractType(e.target.value)}
            >
              <option value="standard">standard</option>
              <option value="trial">trial</option>
              <option value="partner">partner</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="features" className="field-label">
              利用機能(カンマ区切り)
            </label>
            <input
              id="features"
              type="text"
              className="input"
              value={featuresCsv}
              onChange={(e) => setFeaturesCsv(e.target.value)}
              placeholder="mail,chatbot,ga4,gbp"
            />
            <div className="field-hint">例: mail / chatbot / ga4 / gbp / instagram など</div>
          </div>

          {error && <div className="login-error">{error}</div>}

          <div className="row" style={{ marginTop: 8 }}>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? "作成中..." : "作成する"}
            </Button>
            <Button variant="ghost" onClick={() => navigate("/rf-admin/tenants")}>
              キャンセル
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
